import {match, Pattern} from 'ts-pattern';

import {RefObject, useEffect} from 'react';

import {isNil, isNotNil} from 'ramda';

import {Nullish, useLatest} from 'shared/web';

import {KeyboardShortcut} from '../../constants/KeyboardShortcut';
import {getIsElementInTopDialog} from './getIsElementInTopDialog';
import {registerShortcut} from './registerShortcut';

type UseKeyboardShortcutParams = {
  shortcut: KeyboardShortcut | Nullish;
  buttonRef: RefObject<HTMLButtonElement | null>;
  linkRef: RefObject<HTMLAnchorElement | null>;
  isDisabled: boolean | Nullish;
  isLoading: boolean | Nullish;
};

/**
 * Registers a global keyboard shortcut that invokes the button via native click().
 * Disabled and loading states are guarded explicitly so non-button elements and
 * loading buttons cannot be invoked programmatically. Buttons covered by an open
 * dialog are skipped so the chord cannot trigger an action behind that dialog.
 */
export function useKeyboardShortcut(params: UseKeyboardShortcutParams) {
  const isDisabledRef = useLatest(params.isDisabled);
  const isLoadingRef = useLatest(params.isLoading);

  // This subscription must be replaced when the shortcut changes, so useOnMount
  // cannot provide the required reactive cleanup lifecycle.
  useEffect(
    function registerKeyboardShortcut() {
      if (isNil(params.shortcut)) {
        return;
      }

      return registerShortcut(params.shortcut, () => {
        const element = params.buttonRef.current ?? params.linkRef.current;

        return match({
          isDisabled: isDisabledRef.current,
          isLoading: isLoadingRef.current,
          element,
        })
          .with({isDisabled: true}, () => false)
          .with({isLoading: true}, () => false)
          .with({element: Pattern.when(isNil)}, () => false)
          .with({element: Pattern.when(isNotNil)}, ({element: activeElement}) => {
            if (!getIsElementInTopDialog(activeElement)) {
              return false;
            }

            activeElement.click();
            return true;
          })
          .exhaustive();
      });
    },
    [params.buttonRef, params.linkRef, params.shortcut, isDisabledRef, isLoadingRef]
  );
}
